import { useCallback, useMemo, useState } from "react"
import { toast } from "sonner"
import {
  EmployeeDocumentsApiError,
  onboardingService,
} from "@/modules/employee/infrastructure/employeeService"
import type {
  EmployeeDocumentItemDto,
  EmployeeDocumentsErrorCode,
} from "@/modules/employee/employee/dto/employeeDocuments"

interface UseEmployeeDocumentsResult {
  onboardingDocuments: EmployeeDocumentItemDto[]
  myDocuments: EmployeeDocumentItemDto[]
  isInitialLoading: boolean
  isUploading: boolean
  uploadProgressPercent: number
  deletingDocumentIds: Set<string>
  inactiveEmployeeBlocked: boolean
  employeeNotFound: boolean
  shouldRelogin: boolean
  genericError: string | null
  uploadError: string | null
  loadDocuments: () => Promise<void>
  uploadDocument: (file: File) => Promise<boolean>
  deleteDocument: (document: EmployeeDocumentItemDto) => Promise<void>
  downloadDocument: (documentId: string) => Promise<void>
}

function toApiError(error: unknown): EmployeeDocumentsApiError {
  if (error instanceof EmployeeDocumentsApiError) {
    return error
  }

  if (error instanceof Error) {
    const fallback = new EmployeeDocumentsApiError(error.message)
    const maybeErrorCode = (error as { errorCode?: string }).errorCode
    if (maybeErrorCode) {
      fallback.errorCode = maybeErrorCode
    }
    return fallback
  }

  return new EmployeeDocumentsApiError(
    "Something went wrong. Please try again.",
  )
}

function isReloginError(errorCode?: string): boolean {
  return errorCode === "USER_NOT_FOUND"
}

export function useEmployeeDocuments(): UseEmployeeDocumentsResult {
  const [onboardingDocuments, setOnboardingDocuments] = useState<
    EmployeeDocumentItemDto[]
  >([])
  const [myDocuments, setMyDocuments] = useState<EmployeeDocumentItemDto[]>([])
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgressPercent, setUploadProgressPercent] = useState(0)
  const [deletingDocumentIds, setDeletingDocumentIds] = useState<Set<string>>(
    new Set(),
  )
  const [inactiveEmployeeBlocked, setInactiveEmployeeBlocked] = useState(false)
  const [employeeNotFound, setEmployeeNotFound] = useState(false)
  const [shouldRelogin, setShouldRelogin] = useState(false)
  const [genericError, setGenericError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const resetNonBlockingErrors = useCallback(() => {
    setGenericError(null)
    setUploadError(null)
  }, [])

  const handleTopLevelError = useCallback((error: unknown) => {
    const parsed = toApiError(error)
    const errorCode = parsed.errorCode as EmployeeDocumentsErrorCode | undefined

    if (errorCode === "EMPLOYEE_NOT_ACTIVE") {
      setInactiveEmployeeBlocked(true)
      setOnboardingDocuments([])
      setMyDocuments([])
      setGenericError(null)
      return
    }

    if (errorCode === "EMPLOYEE_NOT_FOUND") {
      setEmployeeNotFound(true)
      return
    }

    if (isReloginError(errorCode)) {
      setShouldRelogin(true)
      return
    }

    setGenericError(parsed.message || "Something went wrong. Please try again.")
  }, [])

  const loadDocuments = useCallback(async () => {
    setIsInitialLoading(true)
    setShouldRelogin(false)
    setInactiveEmployeeBlocked(false)
    setEmployeeNotFound(false)
    setUploadProgressPercent(0)
    resetNonBlockingErrors()

    try {
      const docs = await onboardingService.getMyDocuments()
      setOnboardingDocuments(docs.onboardingDocuments ?? [])
      setMyDocuments(docs.myDocuments ?? [])
    } catch (error) {
      handleTopLevelError(error)
    } finally {
      setIsInitialLoading(false)
    }
  }, [handleTopLevelError, resetNonBlockingErrors])

  const uploadDocument = useCallback(async (file: File) => {
    setIsUploading(true)
    setUploadProgressPercent(0)
    setUploadError(null)

    try {
      await onboardingService.uploadMyDocument(file, setUploadProgressPercent)
      const refreshedDocuments = await onboardingService.getMyDocuments()
      setOnboardingDocuments(refreshedDocuments.onboardingDocuments ?? [])
      setMyDocuments(refreshedDocuments.myDocuments ?? [])
      return true
    } catch (error) {
      const parsed = toApiError(error)
      const errorCode = parsed.errorCode as
        | EmployeeDocumentsErrorCode
        | undefined

      if (errorCode === "EMPLOYEE_NOT_ACTIVE") {
        setInactiveEmployeeBlocked(true)
        setOnboardingDocuments([])
        setMyDocuments([])
        return false
      }

      if (errorCode === "EMPLOYEE_NOT_FOUND") {
        setEmployeeNotFound(true)
        return false
      }

      if (isReloginError(errorCode)) {
        setShouldRelogin(true)
        return false
      }

      setUploadError(parsed.message || "Failed to upload document")
      return false
    } finally {
      setIsUploading(false)
      setUploadProgressPercent(0)
    }
  }, [])

  const deleteDocument = useCallback(
    async (document: EmployeeDocumentItemDto) => {
      setDeletingDocumentIds((current) => {
        const next = new Set(current)
        next.add(document.id)
        return next
      })

      try {
        await onboardingService.deleteMyDocument(document.id)
        setMyDocuments((current) =>
          current.filter((item) => item.id !== document.id),
        )
        toast.success("Document deleted successfully.")
      } catch (error) {
        const parsed = toApiError(error)
        const errorCode = parsed.errorCode as
          | EmployeeDocumentsErrorCode
          | undefined

        if (errorCode === "DOCUMENT_DELETE_NOT_ALLOWED") {
          toast.error("This document cannot be deleted.")
        } else if (errorCode === "DOCUMENT_DELETE_FORBIDDEN") {
          toast.error("You do not have permission to delete this document.")
        } else if (errorCode === "DOCUMENT_ALREADY_DELETED") {
          setMyDocuments((current) =>
            current.filter((item) => item.id !== document.id),
          )
          toast.info("This document has already been deleted.")
        } else if (errorCode === "EMPLOYEE_NOT_ACTIVE") {
          setInactiveEmployeeBlocked(true)
          setOnboardingDocuments([])
          setMyDocuments([])
        } else if (errorCode === "EMPLOYEE_NOT_FOUND") {
          setEmployeeNotFound(true)
        } else if (isReloginError(errorCode)) {
          setShouldRelogin(true)
        } else {
          toast.error(parsed.message || "Failed to delete document")
        }
      } finally {
        setDeletingDocumentIds((current) => {
          const next = new Set(current)
          next.delete(document.id)
          return next
        })
      }
    },
    [],
  )

  const downloadDocument = useCallback(async (documentId: string) => {
    try {
      const downloadResult =
        await onboardingService.getMyManagedDocumentDownloadUrl(documentId)

      const contentType = downloadResult.contentType?.toLowerCase() ?? ""
      const fileName = downloadResult.fileName?.toLowerCase() ?? ""
      const isPdf = contentType.includes("pdf") || fileName.endsWith(".pdf")

      if (isPdf) {
        window.open(downloadResult.downloadUrl, "_blank", "noopener,noreferrer")
        return
      }

      const link = document.createElement("a")
      link.href = downloadResult.downloadUrl
      link.download = downloadResult.fileName || "document"
      link.rel = "noopener noreferrer"
      link.style.display = "none"

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch {
      toast.error("Failed to download document. Please try again.")
    }
  }, [])

  return useMemo(
    () => ({
      onboardingDocuments,
      myDocuments,
      isInitialLoading,
      isUploading,
      uploadProgressPercent,
      deletingDocumentIds,
      inactiveEmployeeBlocked,
      employeeNotFound,
      shouldRelogin,
      genericError,
      uploadError,
      loadDocuments,
      uploadDocument,
      deleteDocument,
      downloadDocument,
    }),
    [
      onboardingDocuments,
      myDocuments,
      isInitialLoading,
      isUploading,
      uploadProgressPercent,
      deletingDocumentIds,
      inactiveEmployeeBlocked,
      employeeNotFound,
      shouldRelogin,
      genericError,
      uploadError,
      loadDocuments,
      uploadDocument,
      deleteDocument,
      downloadDocument,
    ],
  )
}
